// The rule tree's own terms for what a regulation asks of a cargo. Each regulation names
// these its own way; an app translates each regulation's substance into these terms, and
// the tree never reads a regulation's fields itself.

export const REGULATIONS = ['IMDG', 'CFR49'] as const;
export type Regulation = (typeof REGULATIONS)[number];

export const REQUIREMENTS = [
  'technicalName',
  'requiresMarinePollutantChemicalNameIfDeclaredMP',
  'requiresSelfReactiveDetails',
  'requiresFlashpoint',
  'requiresNEC',
  'requiresStabilized',
  'requiresTemperatureControl',
  'requiresRadionuclideDetails',
  'requiresCriticalitySafetyIndex',
  'requiresFumigationDetails',
  'requiresFireworkClassificationReference',
  'requiresEmergengySchedule',
  'requiresEndOfHoldingTime',
  'requiresDSITCode',
  'requiresSubhazardChemicalNames',
  'requiresOuterPackaging',
  'requiresNetWeight',
  'requiresCapacity',
  'requiresNetWeightOrCapacity',
  'requiresCarbonOfOrganicOriginDeclarationDetails',
  'requiresActualDgWeight',
] as const;
export type RequirementId = (typeof REQUIREMENTS)[number];

export type RequirementAnswer = boolean | 'ifMarinePollutant';

/**
 * What each regulation says about one cargo. A missing answer means the regulation doesn't
 * cover that requirement, which is not the same as answering false.
 */
export type RegulationAnswers = Partial<
  Record<Regulation, Partial<Record<RequirementId, RequirementAnswer>>>
>;

/**
 * Requirements one regulation decides, tried in order: the first that answers wins. Every
 * other requirement holds when any regulation requires it.
 */
export type Decisions = Partial<Record<RequirementId, readonly Regulation[]>>;

export function requirementHolds(
  answers: RegulationAnswers,
  decisions: Decisions,
  id: RequirementId,
  isMarinePollutant: boolean
): boolean {
  const holds = (a: RequirementAnswer | undefined) =>
    a === true || (a === 'ifMarinePollutant' && isMarinePollutant);
  const order = decisions[id];
  if (order) {
    const decider = order.find((r) => answers[r]?.[id] !== undefined);
    return decider !== undefined && holds(answers[decider]?.[id]);
  }
  return REGULATIONS.some((r) => holds(answers[r]?.[id]));
}
